import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './dialog';
import { Button } from './button';
import { Textarea } from './textarea';
import { Label } from './label';
import { Star } from 'lucide-react';
import { cn } from '../../lib/utils';

function StarRating({ value, onChange, label, disabled = false }) {
  const [hovered, setHovered] = useState(0);
  
  return (
    <div className="space-y-1">
      <Label className="text-sm text-muted-foreground">{label}</Label>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            disabled={disabled}
            className={cn(
              "transition-transform hover:scale-110 focus:outline-none",
              disabled && "cursor-not-allowed opacity-50"
            )}
            onMouseEnter={() => !disabled && setHovered(star)}
            onMouseLeave={() => !disabled && setHovered(0)}
            onClick={() => !disabled && onChange(star)}
          >
            <Star
              className={cn(
                "w-6 h-6 transition-colors",
                (hovered || value) >= star
                  ? "fill-yellow-400 text-yellow-400"
                  : "fill-transparent text-muted-foreground"
              )}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

export function RatingDialog({ open, onOpenChange, lease, onSubmit, loading = false }) {
  const [overallRating, setOverallRating] = useState(0);
  const [communication, setCommunication] = useState(0);
  const [responsiveness, setResponsiveness] = useState(0);
  const [propertyCondition, setPropertyCondition] = useState(0);
  const [valueForMoney, setValueForMoney] = useState(0);
  const [reviewText, setReviewText] = useState('');

  const handleSubmit = () => {
    if (overallRating === 0 || communication === 0 || responsiveness === 0 || 
        propertyCondition === 0 || valueForMoney === 0) {
      return;
    }

    onSubmit({
      lease_id: lease.lease_id,
      overall_rating: overallRating,
      categories: {
        communication,
        responsiveness,
        property_condition: propertyCondition,
        value_for_money: valueForMoney,
      },
      review_text: reviewText || null,
    });
  };

  const isValid = overallRating > 0 && communication > 0 && responsiveness > 0 && 
                  propertyCondition > 0 && valueForMoney > 0;

  const landlordName = lease?.landlord?.name || 'your landlord';
  const propertyName = lease?.property?.name || 'Property';
  const unitName = lease?.unit?.unit_name || 'Unit';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]" data-testid="rating-dialog">
        <DialogHeader>
          <DialogTitle>Rate Your Experience</DialogTitle>
          <DialogDescription>
            Share your experience with {landlordName} at {propertyName} - {unitName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Overall Rating */}
          <div className="space-y-2 p-4 bg-muted/50 rounded-lg">
            <Label className="text-base font-medium">Overall Rating</Label>
            <div className="flex justify-center gap-2 py-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className="transition-transform hover:scale-125 focus:outline-none"
                  onClick={() => setOverallRating(star)}
                >
                  <Star
                    className={cn(
                      "w-10 h-10 transition-colors",
                      overallRating >= star
                        ? "fill-yellow-400 text-yellow-400"
                        : "fill-transparent text-muted-foreground"
                    )}
                  />
                </button>
              ))}
            </div>
            {overallRating > 0 && (
              <p className="text-center text-sm text-muted-foreground">
                {overallRating === 1 && 'Poor'}
                {overallRating === 2 && 'Fair'}
                {overallRating === 3 && 'Good'}
                {overallRating === 4 && 'Very Good'}
                {overallRating === 5 && 'Excellent'}
              </p>
            )}
          </div>

          {/* Category Ratings */}
          <div className="grid grid-cols-2 gap-4">
            <StarRating
              label="Communication"
              value={communication}
              onChange={setCommunication}
            />
            <StarRating
              label="Responsiveness"
              value={responsiveness}
              onChange={setResponsiveness}
            />
            <StarRating
              label="Property Condition"
              value={propertyCondition}
              onChange={setPropertyCondition}
            />
            <StarRating
              label="Value for Money"
              value={valueForMoney}
              onChange={setValueForMoney}
            />
          </div>

          {/* Review Text */}
          <div className="space-y-2">
            <Label htmlFor="review">Your Review (Optional)</Label>
            <Textarea
              id="review"
              placeholder="Share your experience with future tenants..."
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              rows={4}
              data-testid="rating-review-text"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!isValid || loading}
            data-testid="rating-submit-btn"
          >
            {loading ? 'Submitting...' : 'Submit Rating'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
